/**
 * Amazon SP-API Client
 */
import { apiClient } from '@/lib/api-client'

const API_BASE = '/api/v1'

// =============================================================================
// TYPES
// =============================================================================

export interface AmazonOAuthStartResponse {
  success: boolean
  authorization_url?: string
  integration_id?: string
  state?: string
  error?: string
}

export interface AmazonStatusResponse {
  success: boolean
  integration: {
    id: string
    status: 'active' | 'inactive' | 'error' | 'pending'
    selling_partner_id: string | null
    marketplace_ids: string[]
    authorized_at: string | null
    last_sync_at: string | null
    error_message: string | null
  } | null
}

export interface AmazonTestResponse {
  success: boolean
  status?: 'active' | 'error'
  error?: string
}

export interface CatalogImage {
  url: string
  variant: string
  width: number | null
  height: number | null
}

export interface CatalogIdentifier {
  type: string
  value: string
  marketplace_id: string | null
}

export interface CatalogItem {
  asin: string
  marketplace_id: string
  title: string | null
  brand: string | null
  manufacturer: string | null
  images: CatalogImage[]
  identifiers: CatalogIdentifier[]
  attributes: Record<string, unknown>
  dimensions: Record<string, unknown> | null
  classifications: Record<string, unknown>[]
  sales_ranks: Record<string, unknown>[]
  product_types: string[]
  relationships: Record<string, unknown>[]
  summaries: Record<string, unknown>[]
  vendor_details: Record<string, unknown>[]
  raw_response: Record<string, unknown> | null
}

export interface CatalogLookupResponse {
  success: boolean
  item?: CatalogItem
  error?: string
}

export interface PricingInfo {
  offer_count?: number
  buy_box_price?: number | null
  buy_box_currency?: string | null
  is_prime?: boolean | null
  lowest_new_price?: number | null
  buy_box_seller_id?: string | null
  fulfillment_channel?: string | null
  shipping_price?: number | null
  error?: string
}

export interface AplusInfo {
  has_aplus?: boolean | null
  record_count?: number
  error?: string
}

export interface FullProductItem extends CatalogItem {
  pricing?: PricingInfo
  aplus?: AplusInfo
  is_own_listing?: boolean
}

export interface FullProductResponse {
  success: boolean
  item?: FullProductItem
  error?: string
}

export interface CatalogSearchResponse {
  success: boolean
  items?: CatalogItem[]
  total_results?: number
  next_token?: string | null
  error?: string
}

// =============================================================================
// OAUTH
// =============================================================================

export async function startAmazonOAuth(
  marketplace: string = 'de'
): Promise<AmazonOAuthStartResponse> {
  const response = await apiClient.post<AmazonOAuthStartResponse>(
    `${API_BASE}/workspace-settings/integrations/amazon/oauth/start`,
    { marketplace }
  )
  return response.data
}

export async function getAmazonStatus(): Promise<AmazonStatusResponse> {
  const response = await apiClient.get<AmazonStatusResponse>(
    `${API_BASE}/workspace-settings/integrations/amazon/status`
  )
  return response.data
}

export async function testAmazonConnection(): Promise<AmazonTestResponse> {
  const response = await apiClient.post<AmazonTestResponse>(
    `${API_BASE}/workspace-settings/integrations/amazon/test`
  )
  return response.data
}

export async function disconnectAmazon(): Promise<{ success: boolean; error?: string }> {
  const response = await apiClient.post(
    `${API_BASE}/workspace-settings/integrations/amazon/disconnect`
  )
  return response.data
}

// =============================================================================
// CATALOG
// =============================================================================

export async function lookupCatalogItem(
  asin: string,
  marketplace: string = 'de'
): Promise<CatalogLookupResponse> {
  const response = await apiClient.post<CatalogLookupResponse>(
    `${API_BASE}/amazon/catalog/lookup`,
    { asin, marketplace }
  )
  return response.data
}

export async function lookupFullProduct(
  asin: string,
  marketplace: string = 'de'
): Promise<FullProductResponse> {
  const response = await apiClient.post<FullProductResponse>(
    `${API_BASE}/amazon/catalog/full`,
    { asin, marketplace }
  )
  return response.data
}

export async function searchCatalog(params: {
  marketplace?: string
  identifiers?: string[]
  identifier_type?: string
  keywords?: string[]
  page_size?: number
  page_token?: string
}): Promise<CatalogSearchResponse> {
  const response = await apiClient.post<CatalogSearchResponse>(
    `${API_BASE}/amazon/catalog/search`,
    { marketplace: 'de', ...params }
  )
  return response.data
}
